/**
 * CandidateDrawer — clickable, right-hand slide-in panel that shows every
 * facet of a candidate: profile · searches they appear in · vacancies/signals
 * they were matched against · outreach history · quick actions.
 *
 * Usage:
 *   <CandidateDrawer candidateId={id} onClose={() => setSel(null)} />
 *
 * Fetches:
 *   GET /candidates/{id}                — base profile
 *   GET /candidates/{id}/context        — searches + matches + outreach
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import {
    X, Linkedin, Mail, Phone, MapPin, Calendar, Building2, ExternalLink,
    Search as SearchIcon, GitMerge, MessageCircle, Send, Loader2, Sparkles,
    Radar, ArrowRight, FileText, Download, Users, Target,
} from "lucide-react";
import CandidateAvatar from "@/components/CandidateAvatar";
import { CvSection } from "@/components/CvSection";

const TAB_META = [
    { id: "profile", labelKey: "drawer.profile", Icon: Sparkles },
    { id: "searches", labelKey: "drawer.searches", Icon: SearchIcon },
    { id: "matches", labelKey: "drawer.matches_tab", Icon: GitMerge },
    { id: "outreach", labelKey: "drawer.outreach", Icon: MessageCircle },
];

export default function CandidateDrawer({ candidateId, onClose }) {
    const { t } = useTranslation();
    const [tab, setTab] = useState("profile");
    const [candidate, setCandidate] = useState(null);
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reverseHunting, setReverseHunting] = useState(false);

    useEffect(() => {
        if (!candidateId) return;
        setLoading(true);
        Promise.all([
            api.get(`/candidates/${candidateId}`),
            api.get(`/candidates/${candidateId}/context`).catch(() => ({ data: { searches: [], matches: [], outreach: [] } })),
        ]).then(([a, b]) => {
            setCandidate(a.data);
            setContext(b.data);
        }).finally(() => setLoading(false));
    }, [candidateId]);

    const startReverseHunt = async () => {
        if (!candidate) return;
        if (!confirm(t("cand_page.reverse_confirm", { name: candidate.name }))) return;
        setReverseHunting(true);
        try {
            const r = await api.post("/hunt-missions", {
                title: i18n.t("candpage.vacancies_for", { name: candidate.name }),
                prompt: `Vind bedrijven met openstaande rollen die matchen met ${candidate.name} (${candidate.title})`,
                mission_type: "candidate_to_vacancies",
                data_source: "perplexity",
                target_country: candidate.location?.split(",").slice(-1)[0]?.trim() || "BE",
                target_role: candidate.title,
                source_candidate_id: candidate.id,
                schedule: "manual",
                run_now: true,
            });
            toast.success(t("cand_page.mission_started", { n: r.data?.first_run?.signals_generated || 0 }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("cand_page.reverse_failed"));
        } finally { setReverseHunting(false); }
    };

    if (!candidateId) return null;
    return (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="candidate-drawer-backdrop">
            <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-[var(--jr-surface)] border-l border-white/10 shadow-2xl overflow-y-auto"
                onClick={(e) => e.stopPropagation()} data-testid="candidate-drawer">
                {loading || !candidate ? (
                    <div className="p-10 text-center text-[var(--jr-text-2)]"><Loader2 className="animate-spin mx-auto" size={20} /></div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="p-6 border-b border-white/8 sticky top-0 bg-[var(--jr-surface)]/95 backdrop-blur z-10">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-4 min-w-0 flex-1">
                                    <CandidateAvatar name={candidate.name} url={candidate.photo_url} size={64} testid="drawer-avatar" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h2 className="font-display text-2xl tracking-tight text-white truncate">{candidate.name}</h2>
                                            {candidate.talent_pool === "cronos" && (
                                                <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[var(--jr-amber)]/20 text-[var(--jr-amber)] border border-[var(--jr-amber)]/30">
                                                    Cronos
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-sm text-[var(--jr-text-2)] mt-0.5">{candidate.title}</div>
                                        <div className="text-xs text-[var(--jr-text-3)] mt-1 flex items-center gap-2 flex-wrap">
                                            {candidate.current_company && (
                                                <span className="inline-flex items-center gap-1"><Building2 size={11}/>{candidate.current_company}</span>
                                            )}
                                            {candidate.location && (
                                                <span className="inline-flex items-center gap-1"><MapPin size={11}/>{candidate.location}</span>
                                            )}
                                            <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-white/5">{candidate.seniority}</span>
                                        </div>
                                    </div>
                                </div>
                                <button onClick={onClose} data-testid="drawer-close" className="p-1 text-[var(--jr-text-3)] hover:text-white">
                                    <X size={20}/>
                                </button>
                            </div>

                            {/* Contact strip — clean: logo-only for LinkedIn (both logo and link click through) */}
                            <div className="mt-4 flex items-center gap-2 flex-wrap">
                                <LinkedInButton url={candidate.linkedin_url} name={candidate.name} variant="pill" testid="drawer-linkedin" resolve={{ type: "candidate", id: candidate.id }} />
                                {candidate.email && (
                                    <a href={`mailto:${candidate.email}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--jr-info)]/40 bg-[var(--jr-info)]/10 text-[var(--jr-info)] hover:bg-[var(--jr-info)]/20 transition-colors text-xs">
                                        <Mail size={12}/> Email
                                    </a>
                                )}
                                {candidate.cv_storage_path && (
                                    <button onClick={async () => {
                                        try {
                                            const r = await api.get(`/talent-pool/${candidate.id}/cv`, { responseType: "blob" });
                                            const url = URL.createObjectURL(r.data);
                                            const a = document.createElement("a");
                                            a.href = url; a.download = candidate.cv_original_filename || `${candidate.name}.pdf`;
                                            a.click(); URL.revokeObjectURL(url);
                                        } catch (e) { toast.error(t("pool.cv_download_fail")); }
                                    }} data-testid="drawer-cv"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5 text-xs">
                                        <Download size={12}/> {t("drawer.download_cv")}
                                    </button>
                                )}
                                <button onClick={startReverseHunt} disabled={reverseHunting}
                                    data-testid="drawer-reverse-hunt"
                                    title={t("cand_page.reverse_title")}
                                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                                    {reverseHunting ? <Loader2 size={12} className="animate-spin"/> : <Radar size={12}/>}
                                    {t("drawer.reverse_hunt")}
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="mt-5 flex items-center gap-1 flex-wrap border-b border-white/8 -mb-6 pb-0">
                                {TAB_META.map((tm) => {
                                    const active = tab === tm.id;
                                    const Icon = tm.Icon;
                                    const count = tm.id === "searches" ? (context?.searches?.length || 0)
                                                : tm.id === "matches" ? ((context?.matches?.length || 0) + (context?.vacancies?.length || 0))
                                                : tm.id === "outreach" ? ((context?.outreach?.length || 0) + (context?.enrollments?.length || 0)) : null;
                                    return (
                                        <button key={tm.id} onClick={() => setTab(tm.id)}
                                            data-testid={`drawer-tab-${tm.id}`}
                                            className={`text-xs px-3 py-2 border-b-2 transition-colors inline-flex items-center gap-1.5 ${active
                                                ? "border-[var(--jr-brand)] text-white"
                                                : "border-transparent text-[var(--jr-text-3)] hover:text-white"}`}>
                                            <Icon size={12}/> {t(tm.labelKey)}
                                            {count != null && count > 0 && (
                                                <span className={`text-[9px] font-mono px-1 py-0 rounded ${active ? "bg-[var(--jr-brand)] text-black" : "bg-white/8 text-[var(--jr-text-3)]"}`}>{count}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            {tab === "profile" && <ProfileTab c={candidate}/>}
                            {tab === "searches" && <SearchesTab items={context?.searches || []} candidateId={candidate.id}/>}
                            {tab === "matches" && <MatchesTab items={context?.matches || []} vacancies={context?.vacancies || []}/>}
                            {tab === "outreach" && <OutreachTab items={context?.outreach || []} candidateId={candidate.id} enrollments={context?.enrollments || []}/>}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export function ProfileTab({ c }) {
    const { t } = useTranslation();
    return (
        <div className="space-y-5">
            {c.summary && (
                <section>
                    <SectionLabel>{t("drawer.summary")}</SectionLabel>
                    <p className="text-sm text-[var(--jr-text-2)] leading-relaxed whitespace-pre-line">{c.summary}</p>
                </section>
            )}
            <section>
                <SectionLabel>{t("drawer.skills")}</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                    {(c.skills || []).map((s, i) => (
                        <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-[var(--jr-text-2)]">{s}</span>
                    ))}
                    {(c.skills || []).length === 0 && <span className="text-xs text-[var(--jr-text-3)]">{t("drawer.no_skills")}</span>}
                </div>
            </section>
            <div className="grid sm:grid-cols-2 gap-4">
                <Kv label={t("drawer.experience")} value={c.years_experience ? `${c.years_experience} ${t("drawer.years")}` : "—"}/>
                <Kv label={t("drawer.availability")} value={c.availability?.replace("_", " ") || "—"}/>
                {c.available_from && <Kv label={t("drawer.available_from")} value={c.available_from}/>}
                <Kv label={t("drawer.languages")} value={(c.languages || []).join(", ") || "—"}/>
                <Kv label={t("drawer.source")} value={
                    c.sourced_by === "apollo_talent_search" ? "Apollo" :
                    c.sourced_by === "perplexity_talent_search" ? "Perplexity" :
                    c.sourced_by || "—"
                }/>
                <Kv label={t("drawer.talent_pool")} value={c.talent_pool === "cronos" ? t("drawer.cronos_internal") : t("drawer.external")}/>
            </div>
            {c.pool_notes && (
                <section>
                    <SectionLabel>{t("drawer.notes")}</SectionLabel>
                    <p className="text-sm text-[var(--jr-text-2)] leading-relaxed whitespace-pre-line">{c.pool_notes}</p>
                </section>
            )}
            <CvSection c={c}/>
        </div>
    );
}

export function SearchesTab({ items, candidateId }) {
    const { t } = useTranslation();
    if (items.length === 0) return <Empty>{t("drawer.no_searches")}</Empty>;
    return (
        <div className="space-y-2">
            {items.map((s) => (
                <Link key={s.id} to={`/app/recruit/search?search=${s.id}`}
                    data-testid={`drawer-search-${s.id}`}
                    className="block rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3 hover:border-white/20 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                            <div className="text-sm text-white truncate">{s.label}</div>
                            <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5 font-mono">
                                {(s.created_at || "").slice(0, 10)} · via {s.source} · {(s.candidate_ids || []).length} kandidaten
                            </div>
                        </div>
                        <ArrowRight size={12} className="text-[var(--jr-text-3)] mt-1"/>
                    </div>
                </Link>
            ))}
        </div>
    );
}

const MATCH_STATUS_STYLE = {
    proposed: "bg-[var(--jr-amber)]/10 text-[var(--jr-amber)]",
    approved: "bg-[#22c55e]/10 text-[#22c55e]",
    rejected: "bg-white/5 text-[var(--jr-text-3)]",
    outreach_started: "bg-[var(--jr-brand)]/12 text-[var(--jr-brand)]",
    meeting: "bg-[var(--jr-info)]/10 text-[var(--jr-info)]",
    won: "bg-[#22c55e]/15 text-[#22c55e]",
};

export function MatchesTab({ items, vacancies = [] }) {
    const { t } = useTranslation();
    if (items.length === 0 && vacancies.length === 0) return <Empty>{t("drawer.no_matches")}</Empty>;
    return (
        <div className="space-y-4">
            {/* Vacatures gevonden via een "Zoek vacatures voor deze kandidaat"-hunt */}
            {vacancies.length > 0 && (
                <section data-testid="drawer-vacancies">
                    <SectionLabel>{t("drawer.vacancies_found")}</SectionLabel>
                    <div className="space-y-2">
                        {vacancies.map((v) => (
                            <div key={v.id} className="rounded-lg border border-[#a855f7]/25 bg-[#a855f7]/[0.04] p-3" data-testid={`drawer-vacancy-${v.id}`}>
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-white truncate inline-flex items-center gap-1.5">
                                            <Radar size={11} className="text-[#a855f7] shrink-0"/> {v.company_name}
                                        </div>
                                        <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5 font-mono">
                                            {[v.industry, v.location].filter(Boolean).join(" · ")}
                                            {v.created_at ? ` · ${v.created_at.slice(0, 10)}` : ""}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        {(v.open_vacancies_count || 0) > 0 && (
                                            <a href={v.careers_page_url || "#"}
                                                target={v.careers_page_url ? "_blank" : undefined} rel="noreferrer"
                                                onClick={(e) => !v.careers_page_url && e.preventDefault()}
                                                data-testid={`drawer-vacancy-jobs-${v.id}`}
                                                className="text-[10px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e] hover:bg-[#22c55e]/20">
                                                {v.open_vacancies_count} {t("drawer.open_jobs")}
                                            </a>
                                        )}
                                        {v.verification && (
                                            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${v.verification.status === "verified" ? "bg-[#22c55e]/12 text-[#22c55e]" : v.verification.status === "partial" ? "bg-[var(--jr-amber)]/12 text-[var(--jr-amber)]" : "bg-[var(--jr-error)]/12 text-[var(--jr-error)]"}`}
                                                title={`${t("verify.trust")} ${v.verification.trust_score}/100`}>
                                                {v.verification.trust_score}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {v.signal_title && <p className="text-xs text-[var(--jr-text-2)] mt-1.5">{v.signal_title}</p>}
                                <div className="mt-2 pt-2 border-t border-white/8 flex items-center justify-end">
                                    <Link to="/app/sales/signals" className="text-[10px] text-[var(--jr-text-3)] hover:text-white inline-flex items-center gap-1"
                                        data-testid={`drawer-vacancy-link-${v.id}`}>
                                        {t("drawer.view_in_hunt")} <ArrowRight size={10}/>
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
            {items.length > 0 && (
                <section>
                    {vacancies.length > 0 && <SectionLabel>{t("drawer.matches_label")}</SectionLabel>}
                    <div className="space-y-2">
                        {items.map((m) => {
                const status = MATCH_STATUS_STYLE[m.status] ? m.status : "proposed";
                return (
                    <div key={m.id} className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3" data-testid={`drawer-match-${m.id}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-white truncate">{m.company_name || m.signal_title || "—"}</div>
                                <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5 font-mono">
                                    {m.signal_type ? `${m.signal_type} · ` : ""}
                                    {m.direction === "talent_to_signal" ? t("drawer.dir_talent_to_signal") : t("drawer.dir_signal_to_talent")}
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                <span className={`text-[10px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${MATCH_STATUS_STYLE[status]}`}
                                    data-testid={`drawer-match-status-${m.id}`}>
                                    {t(`matches.status.${status}`)}
                                </span>
                                <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[var(--jr-brand)]/15 text-[var(--jr-brand)]">
                                    {m.confidence}%
                                </span>
                            </div>
                        </div>
                        {m.rationale && <p className="text-xs text-[var(--jr-text-2)] mt-1.5">{m.rationale}</p>}
                        {m.status === "outreach_started" && (
                            <div className="mt-2 pt-2 border-t border-white/8 flex items-center justify-between gap-2">
                                <span className="inline-flex items-center gap-1.5 text-[10px] text-[var(--jr-brand)]">
                                    <Send size={10}/> {t("drawer.outreach_running")}
                                </span>
                                <Link to="/app/recruit/outreach" className="text-[10px] text-[var(--jr-text-3)] hover:text-white inline-flex items-center gap-1"
                                    data-testid={`drawer-match-outreach-link-${m.id}`}>
                                    {t("drawer.view_outreach")} <ArrowRight size={10}/>
                                </Link>
                            </div>
                        )}
                    </div>
                );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
}

const ENR_STATUS_STYLE = {
    queued: "bg-white/6 text-[var(--jr-text-2)]",
    running: "bg-[#22c55e]/12 text-[#22c55e]",
    waiting_delay: "bg-[var(--jr-amber)]/12 text-[var(--jr-amber)]",
    waiting_for_accept: "bg-[var(--jr-amber)]/12 text-[var(--jr-amber)]",
    awaiting_approval: "bg-[var(--jr-amber)]/12 text-[var(--jr-amber)]",
    completed: "bg-[var(--jr-info)]/12 text-[var(--jr-info)]",
    stopped: "bg-[var(--jr-error)]/12 text-[var(--jr-error)]",
    paused: "bg-white/6 text-[var(--jr-text-3)]",
};

export function OutreachTab({ items, candidateId, enrollments = [] }) {
    const { t } = useTranslation();
    if (items.length === 0 && enrollments.length === 0) {
        return (
            <div className="text-center py-8">
                <Empty>{t("drawer.no_outreach")}</Empty>
                <Link to={`/app/recruit/outreach?candidate=${candidateId}`}
                    className="inline-flex items-center gap-1.5 mt-4 text-xs px-4 py-2 rounded-full bg-[var(--jr-amber)] text-black font-medium hover:bg-[var(--jr-amber)]/85">
                    <Send size={12}/> {t("drawer.start_outreach")}
                </Link>
            </div>
        );
    }
    return (
        <div className="space-y-4">
            {enrollments.length > 0 && (
                <section data-testid="drawer-enrollments">
                    <SectionLabel>{t("drawer.campaigns_label")}</SectionLabel>
                    <div className="space-y-2">
                        {enrollments.map((e) => (
                            <Link key={e.id} to="/app/recruit/campaigns"
                                data-testid={`drawer-enrollment-${e.id}`}
                                className="block rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3 hover:border-white/20 transition-colors">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-white truncate inline-flex items-center gap-1.5">
                                            <Target size={11} className="text-[var(--jr-amber)] shrink-0"/> {e.campaign_name}
                                        </div>
                                        <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5 font-mono">
                                            {t("drawer.step_of", { cur: (e.current_step_index || 0) + 1, total: Math.max(e.total_steps || 0, (e.current_step_index || 0) + 1) })}
                                            {" · "}{(e.created_at || "").slice(0, 10)}
                                        </div>
                                    </div>
                                    <span className={`text-[10px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded shrink-0 ${ENR_STATUS_STYLE[e.status] || ENR_STATUS_STYLE.queued}`}>
                                        {t(`drawer.enr_status.${e.status}`, e.status)}
                                    </span>
                                </div>
                                {e.stop_reason && (
                                    <div className="text-[10px] text-[var(--jr-text-3)] mt-1 italic">{e.stop_reason}</div>
                                )}
                            </Link>
                        ))}
                    </div>
                </section>
            )}
            {items.length > 0 && (
                <section>
                    {enrollments.length > 0 && <SectionLabel>{t("drawer.messages_label")}</SectionLabel>}
                    <div className="space-y-2">
                        {items.map((o) => (
                            <div key={o.id} className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3">
                                <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                                    <span>{o.channel}</span>
                                    <span className={o.direction === "inbound" ? "text-[#22c55e]" : "text-[var(--jr-brand)]"}>
                                        {o.direction === "inbound" ? t("drawer.received") : t("drawer.sent")}
                                    </span>
                                    {o.status && <span className="px-1 py-0 rounded bg-white/6 normal-case">{o.status}</span>}
                                    <span>· {(o.sent_at || o.created_at || "").slice(0, 10)}</span>
                                </div>
                                {o.subject && <div className="text-xs font-medium text-white mt-1">{o.subject}</div>}
                                <p className="text-xs text-[var(--jr-text-2)] mt-1 line-clamp-3 whitespace-pre-wrap">{o.body}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

export function SectionLabel({ children }) {
    return <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">{children}</div>;
}
export function Kv({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</div>
            <div className="text-sm text-white mt-0.5">{value}</div>
        </div>
    );
}
export function Empty({ children }) {
    return <div className="text-center py-8 text-xs text-[var(--jr-text-3)]">{children}</div>;
}
